import { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { 
  DeleteButton,
  ExportButton,
  FormButton,
  InfoButton,
  SuccessButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import * as actions from "../../_redux/course-subjects/CourseSubjectsAction";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { CourseSubjectsFilter } from "./course-subjects-filter/CourseSubjectsFilter";
import { CourseSubjectsTable } from "./course-subjects-table/CourseSubjectsTable";
import { useCourseSubjectsUIContext } from "./CourseSubjectsUIContext";

export function CourseSubjectsCard() {
  // Course Subjects UI Context
  const courseSubjectsUIContext = useCourseSubjectsUIContext();
  const courseSubjectsUIProps = useMemo(() => {
    return {
      ids: courseSubjectsUIContext.ids,
      setIds: courseSubjectsUIContext.setIds,
      queryParams: courseSubjectsUIContext.queryParams,
      setQueryParams: courseSubjectsUIContext.setQueryParams,
      openImportFromExcelDialog: courseSubjectsUIContext.openImportFromExcelDialog,
      openCourseListPage: courseSubjectsUIContext.openCourseListPage,
      openNewCourseSubjectPage: courseSubjectsUIContext.openNewCourseSubjectPage
    };
  }, [courseSubjectsUIContext]);
    
  // Getting curret state of Course Subject list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.courseSubjects }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, course, authorities } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Course Subjects Redux state
  const dispatch = useDispatch();

  // Kimlik, Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Ders Konusu Listesini Excele Aktarır */
  const exportSubjectsToExcel = () => {
    dispatch(actions.getAllCourseSubjects(courseSubjectsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [30, 15, 45];
      const columns = ["Konu", "Kodu", "Açıklaması"];
      entities.forEach(entity => rows.push([entity.subject, entity.code, entity.explanation]));
      const filename = course + " Ders Konusu Listesi";
      const sheetname = filename.length <= 31 ? filename : "Ders Konusu Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });    
  }

  /** Belli Bir Ders Konusu Listesini PDF e Aktarır */  
  const exportSubjectsToPDF = () => {
    dispatch(actions.getAllCourseSubjects(courseSubjectsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "Konu",
          rows: entities.map(entity => entity.subject)
        },
        {
          header: "Kodu",
          rows: entities.map(entity => entity.code)
        },
        {
          header: "Açıklaması",
          rows: entities.map(entity => entity.explanation)
        }
      ];
  
      const filename = course + " Ders Konusu Listesi";
      exportToPDF(views, filename);
    });    
  }

  /** Belli Bir Ders Konusu Listesini Yazdırma Penceresini Açar */  
  const printSubjects = () => {
    dispatch(actions.getAllCourseSubjects(courseSubjectsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = course + " Ders Konusu Listesi";
      const properties = ["Konu", "Kodu", "Açıklaması"];
  
      entities.forEach(entity => {
        data.push({
          "Konu": entity.subject,
          "Kodu": entity.code,
          "Açıklaması": entity.explanation
        });
      });
  
      print({ data, properties, header });
    });  
  }

  /** Belli Bir Ders Konusu Listesini Panoya Kopyalar */
  const copySubjects = () => {
    dispatch(actions.getAllCourseSubjects(courseSubjectsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const header = course + " Ders Konusu Listesi";
      let columns = ["Konu", "Kodu", "Açıklaması"];
      entities.forEach(entity => rows.push([entity.subject, entity.code, entity.explanation]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Ders Konusu Listesini Siler */
  const deleteCourseSubjects = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();        
      return;
    }
  
    deletionAlert().then(result => {
      if(result.isConfirmed) dispatch(actions.deleteCourseSubjects(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        courseSubjectsUIProps.setIds([]);
        dispatch(actions.getCourseSubjects(courseSubjectsUIProps.queryParams));
      }, 2500);
    }
  };
  
  const title = <>Ders Konu Listesi (<span style={{color:"red"}}>{course}</span>)
                  {courseSubjectsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{courseSubjectsUIProps.ids.length}</span>]</> : ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        <CardHeaderToolbar>
          <InfoButton
            path="Navigation/Arrow-left"
            title="Ders Listesi"
            method={courseSubjectsUIProps.openCourseListPage}
          />
          {
            isExportationAuthorized &&
            <ExportButton
              exportToExcel={exportSubjectsToExcel}
              exportToPDF={exportSubjectsToPDF}
              print={printSubjects}
              copy={copySubjects}
            />
          }
          {
            isAdditionAuthorized &&
            <>
              <SuccessButton
                path="Files/Import"
                title="Excelden Yükle"
                method={courseSubjectsUIProps.openImportFromExcelDialog}
              />
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Ders Konusu"
                method={courseSubjectsUIProps.openNewCourseSubjectPage}
              />
            </>
          }
          {
            isDeletionAuthorized && 
            <DeleteButton
              title="Seçilenleri Sil"
              method={() => deleteCourseSubjects(courseSubjectsUIProps.ids)}
            />
          }
        </CardHeaderToolbar>
      </CardHeader>
      <CardBody>
        <CourseSubjectsFilter />
        <CourseSubjectsTable
          deleteCourseSubjects={deleteCourseSubjects}
        />
      </CardBody>
    </Card>
  );
}